import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Wait before showing — avoids flash on fast/cached responses (skipped when `immediate`). */
const SHOW_DELAY_MS = 150;
/** Minimum time visible once shown — short enough to avoid feeling stuck. */
const MIN_VISIBLE_MS = 180;

/**
 * Debounced loader visibility: show only when `active` stays true past SHOW_DELAY_MS,
 * and keep visible briefly once revealed so fade-out does not flicker.
 * Pass `immediate` for first paint on page entry (no show delay).
 */
export function useSmartBrandedLoader(active: boolean, immediate = false): boolean {
  const [visible, setVisible] = useState(() => Boolean(immediate && active));
  const activeRef = useRef(active);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const shownAtRef = useRef<number | null>(null);

  activeRef.current = active;

  useLayoutEffect(() => {
    if (!active || !immediate) return;
    if (showTimerRef.current != null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (!visible) {
      shownAtRef.current = Date.now();
      setVisible(true);
    }
  }, [active, immediate, visible]);

  useEffect(() => {
    const clearShowTimer = () => {
      if (showTimerRef.current != null) {
        window.clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
    const clearHideTimer = () => {
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
    const clearAllTimers = () => {
      clearShowTimer();
      clearHideTimer();
    };

    if (active) {
      clearHideTimer();
      if (visible) return undefined;
      if (immediate) return undefined;
      clearShowTimer();
      showTimerRef.current = window.setTimeout(() => {
        showTimerRef.current = null;
        if (!activeRef.current) return;
        shownAtRef.current = Date.now();
        setVisible(true);
      }, SHOW_DELAY_MS);
      return clearShowTimer;
    }

    clearShowTimer();
    if (!visible) {
      shownAtRef.current = null;
      return undefined;
    }

    const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : MIN_VISIBLE_MS;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      if (activeRef.current) return;
      shownAtRef.current = null;
      setVisible(false);
    }, remaining);

    return clearHideTimer;
  }, [active, visible, immediate]);

  useEffect(
    () => () => {
      if (showTimerRef.current != null) window.clearTimeout(showTimerRef.current);
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      showTimerRef.current = null;
      hideTimerRef.current = null;
      shownAtRef.current = null;
    },
    [],
  );

  return visible;
}

/** Tracks whether a resource has loaded at least once this mount. */
export function useResourceEverLoaded(hasPayload: boolean): boolean {
  const everLoadedRef = useRef(false);
  if (hasPayload) everLoadedRef.current = true;
  return everLoadedRef.current;
}
